// Theme support — light / dark / system. Persists in localStorage. The actual
// color swap happens via CSS variables on the html element (see styles.css);
// this module just orchestrates which class is applied.

export type ThemePreference = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'chat:theme';

export function readPreference(): ThemePreference {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    /* ignore */
  }
  return 'system';
}

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return true;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function effectiveTheme(pref: ThemePreference): 'light' | 'dark' {
  if (pref === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return pref;
}

export function applyTheme(pref: ThemePreference): void {
  const effective = effectiveTheme(pref);
  const root = document.documentElement;
  root.classList.remove('theme-light', 'theme-dark', 'dark', 'light');
  root.classList.add(`theme-${effective}`);
  // Tailwind dark-mode hook for any rare `dark:` prefixed class.
  if (effective === 'dark') root.classList.add('dark');
}

export function savePreference(pref: ThemePreference): void {
  try {
    localStorage.setItem(STORAGE_KEY, pref);
  } catch {
    /* ignore */
  }
  applyTheme(pref);
}

// Watch the system color-scheme preference and re-apply the effective theme
// when it changes (only relevant when the user has chosen `system`).
export function watchSystemTheme(getPref: () => ThemePreference): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const onChange = () => {
    if (getPref() === 'system') applyTheme('system');
  };
  if (mq.addEventListener) mq.addEventListener('change', onChange);
  else mq.addListener(onChange);
  return () => {
    if (mq.removeEventListener) mq.removeEventListener('change', onChange);
    else mq.removeListener(onChange);
  };
}
